import { useLocaleTag } from "@/lib/i18n/format";
import type { Translate } from "@/lib/i18n/translate";
import { useTranslations } from "next-intl";
import { useCallback, useMemo, useState } from "react";
import { calculateSupportFee } from "./pricing-support-fee";

const UNITS_MIN = 100;
const UNITS_MAX = 10000;
const CUSTOM_CAP = 20;

export const UNIT_PRESETS: readonly number[] = [500, 1000, 2000, 5000];

/** A default tool row in the "Ohne hausverwalter.ai" column. */
export type ToolDef = {
  readonly key: string;
  readonly label: string;
  readonly sub: string;
  /** €/month at 1.000 units (scales linearly per unit). */
  readonly base: number;
  /** slider max at 1.000 units. */
  readonly max: number;
  readonly step: number;
  /** id of the dark info popover, if this row has one. */
  readonly info?: "ms365" | "it" | "dms";
};

/** `useTranslations("public.pricingCalculator")`. */
export function toolDefs(t: Translate): readonly ToolDef[] {
  return [
    {
      key: "dms",
      label: t("dokumentenablage"),
      sub: t("eigenerServerDokumentenmanagementsystem"),
      base: 150,
      max: 1200,
      step: 10,
      info: "dms",
    },
    {
      key: "telefon",
      label: t("telefonanlage"),
      sub: t("lizenzHardwarekostenTarifeAusgenommen"),
      base: 50,
      max: 160,
      step: 5,
    },
    {
      key: "vorgang",
      label: t("vorgangsmanagementPortalloesung"),
      sub: t("vorgaengePortalMailings"),
      base: 500,
      max: 1200,
      step: 20,
    },
    {
      key: "ms365",
      label: t("microsoft365GoogleWorkspace"),
      sub: t("volleLizenzen"),
      base: 75,
      max: 200,
      step: 5,
      info: "ms365",
    },
    {
      key: "it",
      label: t("itDienstleisterFuerServer"),
      sub: t("wartungSupport"),
      base: 50,
      max: 400,
      step: 10,
      info: "it",
    },
    {
      key: "anruf",
      label: t("kiAnrufbeantworter"),
      sub: "",
      base: 25,
      max: 100,
      step: 5,
    },
    {
      key: "etv",
      label: t("onlineHybridEigentuemerversammlungen"),
      sub: "",
      base: 15,
      max: 1000,
      step: 5,
    },
    {
      key: "formulare",
      label: t("uebergabeformularAppZB"),
      sub: "",
      base: 0,
      max: 60,
      step: 5,
    },
    {
      key: "pm",
      label: t("projektmanagement"),
      sub: t("zBAsana"),
      base: 0,
      max: 100,
      step: 5,
    },
    {
      key: "pdf",
      label: t("pdfBearbeitung"),
      sub: t("zBAdobe"),
      base: 0,
      max: 100,
      step: 5,
    },
    {
      key: "wiki",
      label: t("firmenwikiSoftware"),
      sub: t("zBNotion"),
      base: 0,
      max: 100,
      step: 5,
    },
    {
      key: "calendar",
      label: t("terminbuchungsApp"),
      sub: t("zBCalendly"),
      base: 0,
      max: 100,
      step: 5,
    },
  ];
}

export type CustomRow = {
  readonly id: string;
  readonly name: string;
  readonly value: number;
};

/** Snap a raw value to the slider step so display and slider agree. */
function snap(raw: number, step: number): number {
  return Math.round(raw / step) * step;
}

export function fmt(n: number, localeTag: string): string {
  return Math.round(n).toLocaleString(localeTag);
}

/** Two-stop track gradient (verdigris fill up to pct, then faint rest). */
export function trackBg(pct: number): string {
  const p = Math.max(0, Math.min(100, pct));
  return `linear-gradient(90deg, #0B0F19 0%, #0B0F19 ${p}%, rgba(11,15,25,0.12) ${p}%, rgba(11,15,25,0.12) 100%)`;
}

export type DerivedTool = {
  readonly def: ToolDef;
  readonly value: number;
  readonly overridden: boolean;
  readonly sliderMax: number;
  readonly trackBg: string;
};

export type DerivedCustom = {
  readonly row: CustomRow;
  readonly sliderMax: number;
  readonly trackBg: string;
};

export type CalculatorState = {
  readonly units: number;
  readonly unitsLabel: string;
  readonly unitsPct: number;
  readonly unitsTrackBg: string;
  readonly setUnits: (units: number) => void;
  readonly tools: readonly DerivedTool[];
  readonly setOverride: (key: string, value: number) => void;
  readonly resetOverride: (key: string) => void;
  readonly customs: readonly DerivedCustom[];
  readonly addCustom: () => void;
  readonly renameCustom: (id: string, name: string) => void;
  readonly setCustomValue: (id: string, value: number) => void;
  readonly removeCustom: (id: string) => void;
  readonly leftTotalLabel: string;
  readonly leftYearLabel: string;
  readonly hvCostLabel: string;
  readonly supportFeeLabel: string;
  readonly hasSupportFee: boolean;
  readonly msMailLabel: string;
  readonly rightTotalLabel: string;
  readonly rightYearLabel: string;
  readonly saveMoLabel: string;
  readonly saveYrLabel: string;
};

/** All calculator state + derived totals, matching the prototype math. */
export function usePricingCalculator(): CalculatorState {
  const localeTag = useLocaleTag();
  const t = useTranslations("public.pricingCalculator");
  const defs: readonly ToolDef[] = useMemo(() => toolDefs(t), [t]);
  const [units, setUnitsRaw] = useState(1000);
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  const [customRows, setCustomRows] = useState<readonly CustomRow[]>([]);

  const setUnits = useCallback((next: number) => {
    setUnitsRaw(Number.isFinite(next) ? next : 1000);
  }, []);

  const setOverride = useCallback((key: string, value: number) => {
    const n = Math.max(0, Math.round(Number.isFinite(value) ? value : 0));
    setOverrides((prev) => ({ ...prev, [key]: n }));
  }, []);

  const resetOverride = useCallback((key: string) => {
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const addCustom = useCallback(() => {
    setCustomRows((prev) => {
      if (prev.length >= CUSTOM_CAP) return prev;
      const id = `c${Date.now()}-${Math.floor(Math.random() * 1e4)}`;
      return [...prev, { id, name: "", value: 0 }];
    });
  }, []);

  const renameCustom = useCallback((id: string, name: string) => {
    setCustomRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, name } : r)),
    );
  }, []);

  const setCustomValue = useCallback((id: string, value: number) => {
    const n = Math.max(0, Math.round(Number.isFinite(value) ? value : 0));
    setCustomRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, value: n } : r)),
    );
  }, []);

  const removeCustom = useCallback((id: string) => {
    setCustomRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const tools = useMemo<readonly DerivedTool[]>(
    () =>
      defs.map((def) => {
        const override = overrides[def.key];
        const overridden = override != null;
        const value = overridden
          ? override
          : snap((def.base * units) / 1000, def.step);
        const sliderMax = Math.max(
          snap((def.max * units) / 1000, def.step),
          value,
          def.step * 2,
        );
        const pct = sliderMax > 0 ? (value / sliderMax) * 100 : 0;
        return { def, value, overridden, sliderMax, trackBg: trackBg(pct) };
      }),
    [units, overrides, defs],
  );

  const customs = useMemo<readonly DerivedCustom[]>(
    () =>
      customRows.map((row) => {
        const sliderMax = Math.max(300, row.value, 10);
        const pct = sliderMax > 0 ? (row.value / sliderMax) * 100 : 0;
        return { row, sliderMax, trackBg: trackBg(pct) };
      }),
    [customRows],
  );

  const pct = ((units - UNITS_MIN) / (UNITS_MAX - UNITS_MIN)) * 100;

  const leftTotal =
    tools.reduce((sum, t) => sum + t.value, 0) +
    customRows.reduce((sum, c) => sum + c.value, 0);
  const hvCost = Math.round(units * 0.49);
  const supportFee = calculateSupportFee(units);
  const msMail = Math.round(15 + 0.005 * units);
  const rightTotal = hvCost + supportFee + msMail;
  const saveMo = Math.max(0, leftTotal - rightTotal);

  return {
    units,
    unitsLabel: units.toLocaleString(localeTag),
    unitsPct: pct,
    unitsTrackBg: trackBg(pct),
    setUnits,
    tools,
    setOverride,
    resetOverride,
    customs,
    addCustom,
    renameCustom,
    setCustomValue,
    removeCustom,
    leftTotalLabel: fmt(leftTotal, localeTag),
    leftYearLabel: fmt(leftTotal * 12, localeTag),
    hvCostLabel: fmt(hvCost, localeTag),
    supportFeeLabel: fmt(supportFee, localeTag),
    hasSupportFee: supportFee > 0,
    msMailLabel: fmt(msMail, localeTag),
    rightTotalLabel: fmt(rightTotal, localeTag),
    rightYearLabel: fmt(rightTotal * 12, localeTag),
    saveMoLabel: fmt(saveMo, localeTag),
    saveYrLabel: fmt(saveMo * 12, localeTag),
  };
}
